import { MantineProvider } from '@mantine/core';
import type { IconNames } from '@repo/foundations';
import { SecodaTheme } from '@repo/theme/mantine/secodaTheme';
import type { ColorNames } from '@repo/theme/utils';
import { wrappingInputRule } from 'prosemirror-inputrules';
import type { ParseSpec } from 'prosemirror-markdown';
import type {
	Attrs,
	NodeSpec,
	Node as ProsemirrorNode,
} from 'prosemirror-model';
import ReactDOM from 'react-dom';
import toggleWrap from '../commands/toggleWrap';
import { NoticeDropdown } from '../components/NoticeDropdown';
import dictionary from '../dictionary';
import type { MarkdownSerializerState } from '../lib/markdown/serializer';
import noticesRule from '../rules/notices';
import type { NodeOptions } from './Node';
import Node from './Node';

export default class Notice extends Node {
	get styleOptions() {
		return Object.entries({
			info: {
				label: dictionary.info,
				iconName: 'infoCircle',
				iconColor: 'icon/info/default',
			},
			warning: {
				label: dictionary.warning,
				iconName: 'alertTriangle',
				iconColor: 'icon/warning/default',
			},
			tip: {
				label: dictionary.tip,
				iconName: 'bulb',
				iconColor: 'icon/primary/default',
			},
			critical: {
				label: dictionary.critical,
				iconName: 'exclamationCircle',
				iconColor: 'icon/critical/default',
			},
		});
	}

	get name() {
		return 'container_notice';
	}

	get rulePlugins() {
		return [noticesRule];
	}

	get schema(): NodeSpec {
		return {
			attrs: {
				style: {
					default: 'info',
				},
			},
			content: 'block+',
			group: 'block',
			defining: true,
			draggable: true,
			parseDOM: [
				{
					tag: 'div.notice-block',
					preserveWhitespace: 'full',
					contentElement: 'div.content',
					getAttrs: (dom: HTMLElement | string) => {
						if (typeof dom === 'string') {
							return null;
						}

						let style = 'info';

						if (dom.className.includes('tip')) {
							style = 'tip';
						}

						if (dom.className.includes('warning')) {
							style = 'warning';
						}

						if (dom.className.includes('critical')) {
							style = 'critical';
						}

						return {
							style,
						};
					},
				},
			],
			toDOM: (node) => {
				const options = this.styleOptions.map(
					([key, { label, iconName, iconColor }]) => ({
						value: key,
						label,
						iconName: iconName as IconNames,
						iconColor: iconColor as ColorNames,
					})
				);

				const icon = document.createElement('div');
				icon.className = 'icon';
				icon.contentEditable = 'false';
				ReactDOM.render(
					<MantineProvider
						theme={{ ...SecodaTheme, colorScheme: 'light' }}
						withGlobalStyles
						withNormalizeCSS
					>
						<NoticeDropdown
							value={node.attrs.style}
							options={options}
							onChange={this.handleStyleChange}
						/>
					</MantineProvider>,
					icon
				);

				return [
					'div',
					{ class: `block-container notice-block ${node.attrs.style}` },
					icon,
					['div', { class: 'content' }, 0],
				];
			},
		};
	}

	commands({ type }: NodeOptions) {
		return (attrs?: Attrs) => toggleWrap(type, attrs);
	}

	handleStyleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const { view } = this.editorState;
		const { tr } = view.state;
		const element = event.target;
		const { top, left } = element.getBoundingClientRect();
		const result = view.posAtCoords({ top, left });

		if (result) {
			const transaction = tr.setNodeMarkup(result.inside, undefined, {
				style: element.value,
			});
			view.dispatch(transaction);
		}
	};

	inputRules({ type }: NodeOptions) {
		return [wrappingInputRule(/^:::$/, type)];
	}

	toMarkdown(state: MarkdownSerializerState, node: ProsemirrorNode) {
		state.write(`\n:::${node.attrs.style || 'info'}\n`);
		state.renderContent(node);
		state.ensureNewLine();
		state.write(':::');
		state.closeBlock(node);
	}

	parseMarkdown(): ParseSpec {
		return {
			block: 'container_notice',
			getAttrs: (tok) => ({ style: tok.info }),
		};
	}
}
